import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import moment from "moment";
import shortid from "shortid";
import _ from "lodash";
import { Grid, Button } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { getAmortTestData, downloadAmortTestData } from "store/state/calculators/testHarness";
import { LIBOR_FORWARD_CURVE, CAP_VOLS, MARKET_EXPECTATIONS_SHOCK, PARALLEL_SHOCK, STANDARD_DEVIATION_SHOCK, TREASURIES } from "../Constants/Constants";
import DayCount from "../Constants/DayCount";
import InterestRateType from "../Constants/InterestRateType";
import CashflowsType from "../Constants/CashflowsType";
import BusinessDayAdjustmentMethods from "../Constants/BusinessDayAdjustmentMethods";
import InterestRateRounding from "../Constants/InterestRateRounding";
import * as DebtVal from "../Constants/DebtValuation";
import { StyledGrid } from "../Constants/StyledComponents";
import PrimaryButton from "components/Global/Buttons/PrimaryButton";
import PageWrapper from "components/Global/PageOrganization/PageWrapper"
import TablePaginationHelper from "../../../utils/tablePaginationHelper";
import CashflowsResultTable from "./CashflowsResultTable";
import SearchCriteria from "./SearchCriteria";
import CashflowsTestHarnessSearch from "./CashflowsTestHarnessSearch";
import { DebtValuationParams } from "./CashflowsDebtValuation";

const style1 = {
  root: {
    background: "white",
    borderRadius: 0,
    borderBottomRightRadius: 17,
    borderTopRightRadius: 17,
    border: '1px solid grey',
    color: "black",
    height: 30,
    padding: "0 30px",
    float: "right"
  }
};
const CustomButtonLeft = withStyles(style1)(props => <Button {...props} />);

const style2 = {
  root: {
    background: "white",
    borderRadius: 0,
    borderBottomLeftRadius: 17,
    borderTopLeftRadius: 17,
    border: '1px solid grey',
    color: "black",
    height: 30,
    padding: "0 30px",
    float: "right"
  }
};
const CustomButtonRight = withStyles(style2)(props => <Button {...props} />);

export class CashflowsTab extends Component {
  state = {
    CashflowsRate: "",
    CashflowsTerm: "",
    CashflowsType: CashflowsType.INTEREST_ONLY,
    baseDate: moment(),
    curve: LIBOR_FORWARD_CURVE,
    customMonthlyPrincipalReduction: "",
    dayCount: DayCount.ACTUAL_OVER_360,
    interestOnlyPeriod: 0,
    interestRateType: InterestRateType.FIXED_RATE,
    interestRate: '',
    loanAmount: '$25,000,000',
    loanTerm: 120,
    payment: "",
    shockType: MARKET_EXPECTATIONS_SHOCK,
    shockValue: 0,
    spread: "",
    startDate: moment(),
    searchCriteriaEvents: [],
    businessDayAdjustmentMethod: BusinessDayAdjustmentMethods.PRECEDING,
    lookbackDays: 0,
    interestRateRounding: InterestRateRounding.NO_ROUNDING,
    embeddedCap: "",
    embeddedFloor: "",
    partialPayments: [],
    debtValuationParams: DebtValuationParams,
    isCashFlows: false,
    hasDebtValuation: false
  }

  tablePaginationHelper = new TablePaginationHelper("e52dd1f6-33a8-4e8f-bd70-84fefa95a725", 20);

  copyObject = (obj) => {
    return JSON.parse(JSON.stringify(obj));
  }

  submit = () => {
    const options = this.copyObject(this.state);
    this.transformOptions(options, this.tablePaginationHelper.page, this.tablePaginationHelper.rowsPerPage);
    this.closeSearchCriteria();
    this.props.getAmortTestData(options);
  };

  closeSearchCriteria = () => {
    let searchCriteriaEvents = [ ...this.state.searchCriteriaEvents ];
    searchCriteriaEvents.push({ type: 'CLOSE' });
    this.setState({
      searchCriteriaEvents
    })
  };

  completeSearchCriteriaEvent = () => {
    let searchCriteriaEvents = [ ...this.state.searchCriteriaEvents ];
    searchCriteriaEvents.pop();
    this.setState({
      searchCriteriaEvents
    })
  };

  transformOptions = options => {
    options.page = this.tablePaginationHelper.page;
    options.pageSize = this.tablePaginationHelper.rowsPerPage;
    if (options.interestRate !== null)
      options.interestRate = (+options.interestRate.replace(/[^0-9.-]+/g, "")) / 100; //strips out percent sign and casts to number
    else
      delete options.interestRate;
    if (options.loanAmount)
      options.loanAmount = +options.loanAmount.replace(/[^0-9.-]+/g, "") //strips out any non-digit character 
    if (options.spread)
      options.spread = options.spread / 10000; // bp == 1/100 * 0.01
    if (options.CashflowsRate)
      options.CashflowsRate = +options.CashflowsRate.replace(/[^0-9.-]+/g, "") / 100;
    else
      delete options.CashflowsRate;
    if (options.shockType === PARALLEL_SHOCK && options.shockValue && options.shockValue !== 0)
      options.shockValue *= 0.0001;
    else if (options.shockType === MARKET_EXPECTATIONS_SHOCK)
      delete options.shockValue;

    if (options.embeddedCap)
      options.embeddedCap = +options.embeddedCap.replace(/[^0-9.-]+/g, "") / 100;
    else
      delete options.embeddedCap;

    if (options.embeddedFloor)
      options.embeddedFloor = +options.embeddedFloor.replace(/[^0-9.-]+/g, "") / 100;
    else
      delete options.embeddedFloor;

    options.baseDate = moment(options.baseDate).format("YYYY-MM-DD");
    options.startDate = moment(options.startDate).format("YYYY-MM-DD");

    if (options.partialPayments.length > 0) {
      options.partialPaymentParameters = options.partialPayments.map(x => {
        const payment = +x.amount.replace(/[^0-9.-]+/g, "") //strips out any non-digit character 
        const payoffDate = x.date.format("YYYY-MM-DD");
        return {
          payment,
          payoffDate
        };
      });
    }

    options.debtValuationParams.companyId = this.props.authentication.user.contact.companyId;
    if (options.debtValuationParams.spreadInput)
      options.debtValuationParams.spreadInput = +options.debtValuationParams.spreadInput.toString().replace(/[^0-9.-]+/g, "") / 100;
    else
      delete options.debtValuationParams.spreadInput;
  };
  
  downloadAmortTestData = () => {
    const options = { ...this.state };
    this.transformOptions(options, -1, -1);
    this.transformExportOptions(options);
    this.props.downloadAmortTestData(options);
  };

  transformExportOptions = options => {
    options.pageSize = 100000;
  };

  handleChange = ({ target: { name, value } }) => {
    let changeObj = { [ name ]: value };

    if (name === "shockType" && value === MARKET_EXPECTATIONS_SHOCK) {
      changeObj.shockValue = 0;
    } else if (name === "curve" && value === CAP_VOLS) {
      changeObj.shockType = MARKET_EXPECTATIONS_SHOCK;
    } else if (name === "CashflowsType" && value === CashflowsType.STRAIGHT_LINE) {
      //if we're setting back to straight line we need to clear out custom monthly principal reduction and CashflowsRate
      changeObj.customMonthlyPrincipalReduction = "";
      changeObj.CashflowsRate = "";
    } else if (name === "CashflowsType" && value === CashflowsType.STRAIGHT_LINE_CUSTOM_AMOUNT) {
      changeObj.CashflowsRate = "";
    }

    if (name === 'interestRate') {
      let debtValuationParams = this.state.debtValuationParams;
      _.set(debtValuationParams, "discountMethod", DebtVal.DiscountMethod[ DebtVal.FLOATINGDISCOUNT ].value);
      _.set(debtValuationParams, "baseIndex", DebtVal.BaseIndex[ DebtVal.TREASURYS ].value);
      changeObj.debtValuationParams = debtValuationParams;
      changeObj.CashflowsRate = value;
    }

    this.setState(changeObj);
  };

  handleDateChange = date => this.setState({ baseDate: date });

  handleChangePage = (event, newPage) => {
    if (!event) return null;
    this.tablePaginationHelper.page = newPage;
    this.submit();
  };

  handleChangeRowsPerPage = event => {
    this.tablePaginationHelper.rowsPerPage = event.target.value;
    this.submit();
  };

  addPartialPayment = () => {
    const { partialPayments } = this.state;
    partialPayments.push({ id: shortid.generate(), date: moment(), amount: 0.0 })
    this.setState({ partialPayments: partialPayments });
  };

  removePartialPayment = id => {
    const { partialPayments } = this.state;
    let filtered = partialPayments.filter(x => x.id !== id);
    this.setState({ partialPayments: filtered });
  };

  handlePartialPaymentChange = (id, name, value) => {
    const { partialPayments } = this.state;
    let updated = partialPayments.map(x => {
      if (x.id === id)
        x[ name ] = value;
      return x;
    });
    this.setState({ partialPayments: updated });
  };

  onClickCashFlows = isCashFlows => () => {
    if (this.state.isCashFlows === isCashFlows) return;
    this.setState({ isCashFlows });
  };

  render() {
    return (
      <PageWrapper>
        <Grid container>
          <StyledGrid item xs={6} >
            <SearchCriteria
              events={this.state.searchCriteriaEvents}
              onCompleteEvent={this.completeSearchCriteriaEvent}
              downloadTestData={this.downloadAmortTestData}
              tileData={<CashflowsTestHarnessSearch submit={this.submit} downloadTestData={this.downloadAmortTestData} handleChange={this.handleChange} handleDateChange={this.handleDateChange} state={this.state} addPartialPayment={this.addPartialPayment} removePartialPayment={this.removePartialPayment} handlePartialPaymentChange={this.handlePartialPaymentChange} />}
            />
            <PrimaryButton onClick={this.downloadAmortTestData}> Download </PrimaryButton>
          </StyledGrid>
          <StyledGrid item xs={6}>
            <CustomButtonLeft onClick={this.onClickCashFlows(true)} style={this.state.isCashFlows ? { backgroundColor: 'grey', color: 'white' } : {}}> Debt Valuation </CustomButtonLeft>
            <CustomButtonRight onClick={this.onClickCashFlows(false)} style={!this.state.isCashFlows ? { backgroundColor: 'grey', color: 'white' } : {}} > Cashflows </CustomButtonRight>
          </StyledGrid>
        </Grid>
        <CashflowsResultTable
            submit={this.submit}
            handleChange={this.handleChange}
            handleDateChange={this.handleDateChange}
            handleChangePage={this.handleChangePage}
            handleChangeRowsPerPage={this.handleChangeRowsPerPage}
            pageSize={this.tablePaginationHelper.rowsPerPage}
            page={this.tablePaginationHelper.page}
            showDebtValuationResults={this.state.isCashFlows} />
      </PageWrapper>
    );
  }
}

const mapStateToProps = ({ authentication, testHarness }) => ({ authentication, testHarness });
export default compose(connect(mapStateToProps, { getAmortTestData, downloadAmortTestData }))(CashflowsTab);